import type { Product } from "@/contexts/ComparisonContext";
import {
  isUsableLiveTerms,
  type ProviderLiveTerms,
  type ProviderLiveTermsBySlug,
} from "@/hooks/useProviderLiveTerms";
import type { ComparisonListItem, FinancialProductData } from "@/utils/schemaGenerators";

export function getUsableLiveTerms(terms: ProviderLiveTerms | null | undefined): ProviderLiveTerms | null {
  return isUsableLiveTerms(terms) ? terms : null;
}

export function getPreferredRateLabel(terms: ProviderLiveTerms | null | undefined): string | null {
  const usableTerms = getUsableLiveTerms(terms);
  return usableTerms?.effective_rate_label || usableTerms?.nominal_rate_label || null;
}

export function getLiveLoanWithoutUCLabel(
  terms: ProviderLiveTerms | null | undefined,
  fallback: string
): string {
  const creditCheck = getUsableLiveTerms(terms)?.credit_check_label?.toLowerCase();

  if (!creditCheck) return fallback;
  if (creditCheck.includes("utan") || creditCheck.includes("alternativ")) return "Ja";
  return "Nej";
}

export function getLiveLenderBadge(
  terms: ProviderLiveTerms | null | undefined,
  fallback?: string
): string | undefined {
  const lenderCount = getUsableLiveTerms(terms)?.lender_count;
  return lenderCount ? `Jämför ${lenderCount} långivare` : fallback;
}

export function getLiveAmountLabel(
  terms: ProviderLiveTerms | null | undefined,
  fallback: string
): string {
  return getUsableLiveTerms(terms)?.loan_amount_label || fallback;
}

export function getLiveTermLabel(
  terms: ProviderLiveTerms | null | undefined,
  fallback: string
): string {
  return getUsableLiveTerms(terms)?.term_label || fallback;
}

export function getLiveTermsUpdatedLabel(terms: ProviderLiveTerms | null | undefined): string {
  const usableTerms = getUsableLiveTerms(terms);
  const updatedAt = usableTerms?.last_verified_at || usableTerms?.last_scraped_at;

  if (!updatedAt) return "Live-data inväntas";

  const updatedDate = new Date(updatedAt);
  if (Number.isNaN(updatedDate.getTime())) return "Live-data inväntas";

  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(updatedDate);
}

/**
 * A complete, self-framing verification phrase for hero/meta lines.
 * With a real date: "Kontrollerad 18 juni 2026". Without: a graceful
 * "Villkor uppdateras löpande" (avoids the contradictory
 * "Kontrollerad: Live-data inväntas" when no live data is present yet).
 */
export function getLiveTermsVerifiedLabel(terms: ProviderLiveTerms | null | undefined): string {
  const usableTerms = getUsableLiveTerms(terms);
  const updatedAt = usableTerms?.last_verified_at || usableTerms?.last_scraped_at;
  const updatedDate = updatedAt ? new Date(updatedAt) : null;

  if (!updatedDate || Number.isNaN(updatedDate.getTime())) {
    return "Villkor uppdateras löpande";
  }

  return `Kontrollerad ${new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(updatedDate)}`;
}

export function getLiveTermsUpdatedAt(terms: ProviderLiveTerms | null | undefined): string | null {
  const usableTerms = getUsableLiveTerms(terms);
  const updatedAt = usableTerms?.last_verified_at || usableTerms?.last_scraped_at;

  if (!updatedAt) return null;

  const updatedDate = new Date(updatedAt);
  if (Number.isNaN(updatedDate.getTime())) return null;

  return updatedDate.toISOString();
}

export function getLatestLiveTermsUpdatedAt(termsBySlug: ProviderLiveTermsBySlug): string | undefined {
  const latestTimestamp = Object.values(termsBySlug).reduce<number | null>((latest, terms) => {
    const updatedAt = getLiveTermsUpdatedAt(terms);
    if (!updatedAt) return latest;

    const timestamp = new Date(updatedAt).getTime();
    if (Number.isNaN(timestamp)) return latest;

    return latest === null || timestamp > latest ? timestamp : latest;
  }, null);

  return latestTimestamp === null ? undefined : new Date(latestTimestamp).toISOString();
}

export function applyLiveTermsToProduct(
  product: Product,
  terms: ProviderLiveTerms | null | undefined
): Product {
  const usableTerms = getUsableLiveTerms(terms);
  if (!usableTerms) return product;

  return {
    ...product,
    rate: usableTerms.effective_rate_label || usableTerms.nominal_rate_label || product.rate,
    amount: usableTerms.loan_amount_label || product.amount,
  };
}

export function applyLiveTermsToComparisonItem(
  item: ComparisonListItem,
  terms: ProviderLiveTerms | null | undefined
): ComparisonListItem {
  const usableTerms = getUsableLiveTerms(terms);
  if (!usableTerms) return item;

  return {
    ...item,
    amount: usableTerms.loan_amount_label || item.amount,
    interestRate: usableTerms.effective_rate_label || usableTerms.nominal_rate_label || item.interestRate,
    term: usableTerms.term_label || item.term,
    creditCheck: usableTerms.credit_check_label || item.creditCheck,
  };
}

export function applyLiveTermsToComparisonItems(
  items: ComparisonListItem[],
  termsBySlug: ProviderLiveTermsBySlug
): ComparisonListItem[] {
  return items.map((item) => {
    const slug = item.url.split("/").filter(Boolean).pop();
    return applyLiveTermsToComparisonItem(item, slug ? termsBySlug[slug] : null);
  });
}

export function applyLiveTermsToFinancialProductData(
  product: FinancialProductData,
  terms: ProviderLiveTerms | null | undefined
): FinancialProductData {
  const usableTerms = getUsableLiveTerms(terms);
  if (!usableTerms) return product;

  const rateMin = usableTerms.effective_rate_min ?? usableTerms.nominal_rate_min;
  const rateMax = usableTerms.effective_rate_max ?? usableTerms.nominal_rate_max ?? rateMin;

  return {
    ...product,
    updatedAt: getLiveTermsUpdatedAt(usableTerms) || product.updatedAt,
    interestRate:
      rateMin !== null && rateMin !== undefined && rateMax !== null && rateMax !== undefined
        ? { min: rateMin, max: rateMax }
        : product.interestRate,
    amount: {
      min: usableTerms.loan_amount_min ?? product.amount.min,
      max: usableTerms.loan_amount_max ?? product.amount.max,
    },
  };
}
